"use client";

import {
  ChakraProvider,
  createSystem,
  defaultConfig,
  defineConfig,
} from "@chakra-ui/react";
import * as React from "react";
import type { PropsWithChildren } from "react";

import buttonRecipe from "./recipes/button";
import selectRecipe from "./recipes/select";

const config = defineConfig({
  strictTokens: true,
  globalCss: {
    body: {
      colorPalette: "blue",
    },
  },
  theme: {
    tokens: {
      borderWidths: {
        0: { value: "0px" },
        1: { value: "1px" },
        2: { value: "2px" },
      },
      colors: {
        accent: {
          DEFAULT: { value: "#4988F5" },
          950: { value: "#4988F5E5" },
        },
        inherit: { value: "inherit" },
        navy: {
          DEFAULT: { value: "#09255B" },
          950: { value: "#3B6DC3" },
        },
      },
      fonts: {
        body: { value: "var(--font-outfit)" },
      },
      opacity: {
        0: { value: "0" },
      },
      spacing: {
        0: { value: "0" },
      },
      sizes: {
        auto: { value: "auto" },
        0: { value: "0" },
      },
      gradients: {
        icon: {
          circle: {
            DEFAULT: {
              value: `
linear-gradient(130.5deg, #3F7FFB 17.9%, #88DAFF 115.24%)
`,
            },

            flat: {
              value: "#88DAFF",
            },
          },
        },
      },
      shadows: {
        none: { value: "none" },
        button: {
          solid: {
            DEFAULT: {
              value: `
0px 1px 2px 0px #0A0D120D,
0px -4px 0px 0px #0A0D120D inset,
0px -1px 0px 2px #0A0D122E inset
`,
            },
            active: {
              value: `
0px 1px 2px 0px colorPalette.solid/70,
0px 0px 0px 1px colorPalette.solid
`,
            },
          },
          surface: {
            DEFAULT: {
              value: `
0px 1px 2px 0px #0000000D
`,
            },
          },
        },
        icon: {
          circle: {
            DEFAULT: {
              value: `
0px 1px 3px 0px #0000001A,
0px 1px 2px -1px #0000001A
`,
            },

            flat: {
              value: "none",
            },
          },
        },
      },
    },
    semanticTokens: {
      colors: {
        bg: {
          DEFAULT: { value: "{colors.white}" },
          subtle: { value: "{colors.gray.50}" },
          muted: { value: "{colors.gray.100}" },
          emphasized: { value: "{colors.gray.200}" },
          inverted: { value: "{colors.black}" },
          panel: { value: "{colors.white}" },
          error: { value: "{colors.red.50}" },
          warning: { value: "{colors.orange.50}" },
          success: { value: "{colors.green.50}" },
          info: { value: "{colors.blue.50}" },
          solid: { value: "{colors.white}" },
        },
        border: {
          DEFAULT: { value: "{colors.gray.200}" },
          subtle: { value: "{colors.gray.50}" },
          muted: { value: "{colors.gray.100}" },
          emphasized: { value: "{colors.gray.300}" },
          inverted: { value: "{colors.gray.800}" },
          error: { value: "{colors.red.500}" },
          warning: { value: "{colors.orange.500}" },
          success: { value: "{colors.green.500}" },
          info: { value: "{colors.blue.500}" },
        },
        fg: {
          DEFAULT: { value: "{colors.black}" },
          accent: { value: "{colors.navy}" },
          muted: { value: "{colors.gray.500}" },
          solid: { value: "{colors.white}" },
        },
        blue: {
          contrast: { value: "{colors.white}" },
          fg: { value: "{colors.accent}" },
          solid: { value: "{colors.accent}" },
        },
      },
      radii: {
        l1: { value: "0.125rem" },
        l2: { value: "0.25rem" },
        l3: { value: "0.375rem" },
      },
    },
    recipes: {
      button: buttonRecipe,
    },
    slotRecipes: {
      select: selectRecipe,
    },
  },
});

const system = createSystem(defaultConfig, config);
export default system;

export const Provider = (props: PropsWithChildren) => (
  <ChakraProvider value={system}>{props.children}</ChakraProvider>
);
